import { useMemo } from 'react'

import { PlainSourceRow, SourcesStrip, platformMyResources, type MyResource } from '@krp/platform-ui'

import { SOURCES_STRIP_ROWS } from '../../composition/shell'
import { CONNECTION_FORMS } from '../../composition/connectionForms'

/** Источники, из которых наполняется общий каталог: те же «мои ресурсы» с `catalog.read`, что и в выборе источника. */
const CATALOG_ARGS = { capability: 'catalog.read' } as const
/** Список зависит от прав: окно, открытое заново, читает его заново, а не из кэша. */
const FRESH_RIGHTS = { refetchOnMountOrArgChange: true } as const

/**
 * Мои источники каталога (RP-SRC1, Е-4): их имена нужны свёрнутой строке-триггеру «Источники» в шапке каталога, а сами
 * ресурсы — раскрытому списку. Запрос один на оба применения (общий кэш RTK Query).
 */
export function useMyCatalogSources(): readonly MyResource[] {
  const sources = platformMyResources.useQuery(CATALOG_ARGS, FRESH_RIGHTS)
  // Общий порядок источников (решение владельца 03.11.2026): по виду, внутри вида по названию.
  return useMemo(() => CONNECTION_FORMS.sortSources(sources.data ?? []), [sources.data])
}

/**
 * Строки раскрытого списка «Источники» над «Общим каталогом» (RP-SE13): все источники человека — любого вида. Как проверяется
 * свежесть, решает вклад вида из реестра оболочки (`composition/shell`); у вида без вклада — простая строка. Платформа вида не знает.
 */
export function SourcesRows({ resources }: { resources: readonly MyResource[] }) {
  if (resources.length === 0) return null
  return (
    <SourcesStrip>
      {resources.map((resource) => {
        const Row = SOURCES_STRIP_ROWS[resource.kind]
        return Row === undefined ? (
          <PlainSourceRow
            key={resource.id}
            id={resource.id}
            name={resource.displayName}
            kindTitle={CONNECTION_FORMS.shortTitleOf(resource.kind)}
            tone={CONNECTION_FORMS.toneOf(resource.kind)}
          />
        ) : (
          <Row key={resource.id} resource={resource} />
        )
      })}
    </SourcesStrip>
  )
}
